// src/pages/AdminDashboard.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import axios from "../utils/axios";

const AdminDashboard = () => {
  const { logout, adminData } = useAuth();
  const navigate = useNavigate();

  // State for dashboard stats
  const [stats, setStats] = useState({
    totalSubscribers: 0,
    activeCampaigns: 0,
    emailsSentToday: 0,
    openRate: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch dashboard stats on component mount
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoadingStats(true);
      const response = await axios.get("/api/admin/dashboard-stats");
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      // Keep default values if fetch fails
    } finally {
      setLoadingStats(false);
    }
  };

  const adminPages = [
    {
      path: "/email-marketing",
      title: "Email Marketing",
      icon: "📧",
      description: "Manage email campaigns and subscribers",
      color: "primary",
    },
    {
      path: "/email-ads",
      title: "Email Ads",
      icon: "📢",
      description: "Create and manage email advertisements",
      color: "success",
    },
    {
      path: "/admin-management",
      title: "Admin Management",
      icon: "👥",
      description: "Create and manage admin accounts",
      color: "danger",
      superAdminOnly: true,
    },
    {
      path: "/testpage",
      title: "Test Page",
      icon: "🧪",
      description: "Testing and development features",
      color: "warning",
    },
    {
      path: "/admin-settings",
      title: "Admin Settings",
      icon: "⚙️",
      description: "Configure system settings",
      color: "info",
    },
    {
      path: "/analytics",
      title: "Analytics",
      icon: "📊",
      description: "View site and campaign analytics",
      color: "secondary",
    },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <Layout>
      {/* Admin Alert Banner */}
      <div className="bg-danger text-white py-2">
        <div className="container d-flex justify-content-between align-items-center">
          <div>
            <strong>🛡️ Admin Mode Active</strong> - You have full access to
            administrative features
            {adminData?.is_super_admin && (
              <span className="badge bg-light text-danger ms-2">
                Super Admin
              </span>
            )}
          </div>
          <button onClick={handleLogout} className="btn btn-light btn-sm">
            Logout Admin
          </button>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div>
            <h1 className="display-4">Admin Dashboard</h1>
            <p className="text-muted">
              Welcome back, <strong>{adminData?.username || "Admin"}</strong>
            </p>
          </div>
          <Link to="/" className="btn btn-outline-primary">
            <i className="bi bi-arrow-left me-2"></i>Back to Site
          </Link>
        </div>

        {/* Quick Stats */}
        <div className="row mb-5">
          <div className="col-md-3 mb-3">
            <div className="card bg-primary text-white h-100">
              <div className="card-body">
                <h6 className="card-title text-white-50">Total Subscribers</h6>
                {loadingStats ? (
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <>
                    <h2 className="mb-0">
                      {formatNumber(stats.totalSubscribers)}
                    </h2>
                    <small className="text-white-50">
                      {stats.totalSubscribers > 0
                        ? "Active subscribers"
                        : "No subscribers yet"}
                    </small>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card bg-success text-white h-100">
              <div className="card-body">
                <h6 className="card-title text-white-50">Active Campaigns</h6>
                {loadingStats ? (
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <>
                    <h2 className="mb-0">{stats.activeCampaigns}</h2>
                    <small className="text-white-50">
                      {stats.activeCampaigns > 0
                        ? "Last 30 days"
                        : "No active campaigns"}
                    </small>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card bg-warning text-white h-100">
              <div className="card-body">
                <h6 className="card-title text-white-50">Emails Sent Today</h6>
                {loadingStats ? (
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <>
                    <h2 className="mb-0">
                      {formatNumber(stats.emailsSentToday)}
                    </h2>
                    <small className="text-white-50">
                      {stats.emailsSentToday > 0
                        ? "Sent successfully"
                        : "No emails sent today"}
                    </small>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card bg-info text-white h-100">
              <div className="card-body">
                <h6 className="card-title text-white-50">Open Rate</h6>
                {loadingStats ? (
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <>
                    <h2 className="mb-0">{stats.openRate}%</h2>
                    <small className="text-white-50">
                      {stats.openRate > 21.3
                        ? "Above industry avg"
                        : "Industry avg: 21.3%"}
                    </small>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Refresh Stats Button */}
        <div className="text-end mb-3">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={fetchDashboardStats}
            disabled={loadingStats}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh Stats
          </button>
        </div>

        {/* Admin Tools Grid */}
        <h3 className="mb-4">Administrative Tools</h3>
        <div className="row mb-5">
          {adminPages.map((page) => {
            // Skip super admin only pages if user is not super admin
            if (page.superAdminOnly && !adminData?.is_super_admin) {
              return null;
            }

            return (
              <div key={page.path} className="col-md-4 mb-4">
                <div
                  className={`card h-100 shadow-sm border-0 admin-tool-card`}
                  onClick={() => navigate(page.path)}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.3s",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 20px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <div
                    className={`card-header bg-${page.color} bg-opacity-10 border-0`}
                  >
                    <span style={{ fontSize: "2.5rem" }}>{page.icon}</span>
                    {page.superAdminOnly && (
                      <span className="badge bg-danger position-absolute top-0 end-0 m-2">
                        Super Admin
                      </span>
                    )}
                  </div>
                  <div className="card-body">
                    <h5 className="card-title">{page.title}</h5>
                    <p className="card-text text-muted">{page.description}</p>
                    <div className="text-end">
                      <span className={`text-${page.color} fw-bold`}>
                        Manage <i className="bi bi-arrow-right"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Activity */}
        <div className="row">
          <div className="col-lg-8">
            <h3 className="mb-4">Recent Activity</h3>
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <tbody>
                      <tr>
                        <td width="50">📧</td>
                        <td>
                          Email campaign "Spring Promotion" sent to{" "}
                          {stats.totalSubscribers > 0
                            ? stats.totalSubscribers
                            : "0"}{" "}
                          subscribers
                        </td>
                        <td className="text-muted text-end">2 hours ago</td>
                      </tr>
                      <tr>
                        <td>👤</td>
                        <td>New subscriber: john.doe@example.com</td>
                        <td className="text-muted text-end">5 hours ago</td>
                      </tr>
                      <tr>
                        <td>⚙️</td>
                        <td>System settings updated by admin</td>
                        <td className="text-muted text-end">1 day ago</td>
                      </tr>
                      <tr>
                        <td>📊</td>
                        <td>Monthly analytics report generated</td>
                        <td className="text-muted text-end">2 days ago</td>
                      </tr>
                      <tr>
                        <td>🚫</td>
                        <td>3 users unsubscribed from mailing list</td>
                        <td className="text-muted text-end">3 days ago</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <h3 className="mb-4">Quick Actions</h3>
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate("/email-marketing")}
                  >
                    <i className="bi bi-send me-2"></i>Send Email Campaign
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => navigate("/email-ads")}
                  >
                    <i className="bi bi-megaphone me-2"></i>Create New Ad
                  </button>
                  {adminData?.is_super_admin && (
                    <button
                      className="btn btn-danger"
                      onClick={() => navigate("/admin-management")}
                    >
                      <i className="bi bi-person-plus me-2"></i>Add New Admin
                    </button>
                  )}
                  <button
                    className="btn btn-info"
                    onClick={() => navigate("/analytics")}
                  >
                    <i className="bi bi-graph-up me-2"></i>View Analytics
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={() => navigate("/admin-settings")}
                  >
                    <i className="bi bi-gear me-2"></i>System Settings
                  </button>
                </div>
              </div>
            </div>

            {/* System Status */}
            <div className="card shadow-sm mt-4">
              <div className="card-body">
                <h5 className="card-title">System Status</h5>
                <div className="small">
                  <div className="d-flex justify-content-between mb-2">
                    <span>API Status</span>
                    <span className="badge bg-success">Operational</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Email Service</span>
                    <span className="badge bg-success">Active</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Database</span>
                    <span className="badge bg-success">Connected</span>
                  </div>
                  {adminData?.is_super_admin && (
                    <div className="d-flex justify-content-between">
                      <span>Admin Users</span>
                      <span className="badge bg-info">Active</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
